import {optimisticCacheProduct} from '@/cart/optimistic-updates/optimistic-cache-product';
import CollectionBuyStack from '@/components/collection/CollectionBuyStack';
import {productSchema} from '@/product/types';
import {setProductSellingPlans} from '@/selling-plan/selling-plan-store';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {logError, logInfo} from '@/utilities/log';
import {preloadImage} from '@/utilities/preload-image';
import {Plan} from '@rechargeapps/storefront-client';
import register from 'preact-custom-element';

/**
 * Props for the CollectionBuyStackLoader component
 */
type CollectionBuyStackLoaderProps = {
  /**
   * The JSON stringified product data, passed in via liquid
   */
  product: string;
};

/**
 * Custom element that renders the CollectionBuyStack component
 * @param product - The JSON stringified product data, passed in via liquid
 *
 * @returns A CollectionBuyStack component
 */
function CollectionBuyStackLoader({
  product: productJson,
}: CollectionBuyStackLoaderProps) {
  const product = loadProductData(productJson);
  if (!product) return <></>;

  return <CollectionBuyStack product={product} />;
}

function loadProductData(productJson: string) {
  try {
    const product = productSchema.parse(JSON.parse(productJson));
    // Use the product metafield data to set the selling plans
    // This saves us a GET request to Recharge
    const plans = product.rechargePlans;
    if (plans) {
      setProductSellingPlans(product.id, plans as Plan[]);
    }

    // Cache the product data so we can display faster in the cart
    optimisticCacheProduct(product);

    // Preload the variant images for smoother transitionss
    product.variants.forEach(
      ({image}) =>
        !!image && preloadImage(createShopifyImageUrl(image.url, 440))
    );

    logInfo('CollectionProductLoaded', {productJson, product});

    return product;
  } catch (error) {
    logError(error, {
      message: 'ErrorLoadingProductData',
      productJson,
    });
    return null;
  }
}
register(CollectionBuyStackLoader, 'collection-buy-stack', ['product']);
